import {Dispatch} from 'redux';

import CartQuery from 'Query/Cart.query';
import {CartTotals as QuoteData} from 'Query/Cart.type';
import {
    CartDispatcher as SourceCartDispatcher,
} from 'SourceStore/Cart/Cart.dispatcher';
import {updateIsLoadingCart, updateTotals} from 'Store/Cart/Cart.action';
import {AddProductToCartOptions} from 'Store/Cart/Cart.type';
import {showNotification} from 'Store/Notification/Notification.action';
import {NotificationType} from 'Store/Notification/Notification.type';
import {NetworkError} from 'Type/Common.type';
import {getAuthorizationToken, refreshAuthorizationToken} from 'Util/Auth/Token';
import {getCartId, setCartId} from 'Util/Cart';
import {getErrorMessage} from 'Util/Request/Error';
import {fetchMutation} from 'Util/Request/Mutation';
import {fetchQuery} from 'Util/Request/Query';

/** @namespace Steinkrueger/Store/Cart/Dispatcher */
export class CartDispatcher extends SourceCartDispatcher {
    async updateInitialCartData(
        dispatch: Dispatch,
        isForCustomer = false,
        disableLoader = false,
    ): Promise<string | null> {
        // Need to get current cart from BE, update cart
        try {
            // ! Get quote token first (local or from the backend) just to make sure it exists

            if (!disableLoader) {
                dispatch(updateIsLoadingCart(true));
            }
            // ! Get quote token first (local or from the backend) just to make sure it exists
            const quoteId = await this._getCartId(dispatch);
            const cart = {
                cartData: {},
            };

            try {
                const fetchedCartData = await fetchQuery(
                    CartQuery.getCartQuery(
                        quoteId || '',
                    ),
                );

                cart.cartData = fetchedCartData.cartData;
            } catch (e) {
                refreshAuthorizationToken();
                await this.createGuestEmptyCart(dispatch);
            }

            const { cartData } = cart;

            await this._updateCartData(cartData, dispatch);

            if (isForCustomer && !getAuthorizationToken()) {
                dispatch(updateIsLoadingCart(false));

                return null;
            }

            if (!disableLoader) {
                dispatch(updateIsLoadingCart(false));
            }

            return null;
        } catch (error) {
            dispatch(updateIsLoadingCart(false));

            return this.createGuestEmptyCart(dispatch);
        }
    }

    async emptyCartData(
        dispatch: Dispatch,
        isForCustomer = false,
        disableLoader = false,
    ): Promise<string | null> {
        try {
            if (!disableLoader) {
                dispatch(updateIsLoadingCart(true));
            }
            // ! Get quote token first (local or from the backend) just to make sure it exists
            const quoteId = await this.createGuestEmptyCart(dispatch);
            const {
                cartData = {},
            } = await fetchQuery(
                CartQuery.getCartQuery(
                    quoteId || '',
                ),
            );

            await this._emptyCartData(cartData, dispatch);

            if (isForCustomer && !getAuthorizationToken()) {
                dispatch(updateIsLoadingCart(false));

                return null;
            }

            if (!disableLoader) {
                dispatch(updateIsLoadingCart(false));
            }

            return null;
        } catch (error) {
            dispatch(updateIsLoadingCart(false));

            return this.createGuestEmptyCart(dispatch);
        }
    }

    async createGuestEmptyCart(dispatch: Dispatch): Promise<string | null> {
        try {
            dispatch(updateIsLoadingCart(true));

            const quoteId = await this._getNewQuoteId();

            setCartId(quoteId);

            dispatch(updateIsLoadingCart(false));

            return quoteId;
        } catch (error) {
            dispatch(showNotification(NotificationType.ERROR, getErrorMessage(error as NetworkError)));

            return null;
        }
    }

    _emptyCartData(cartData: Partial<QuoteData>, dispatch: Dispatch): void {
        const emptyCartData = cartData;

        emptyCartData.items = [];

        dispatch(updateTotals(emptyCartData));
    }

    _updateCartData(cartData: Partial<QuoteData>, dispatch: Dispatch): void {
        dispatch(updateTotals(cartData));
    }

    async addProductToCart(
        dispatch: Dispatch,
        options: AddProductToCartOptions,
    ): Promise<void> {
        const { products = [], cartId: userCartId } = options;

        const cartId = userCartId || getCartId();

        if (!Array.isArray(products) || products.length === 0) {
            dispatch(showNotification(NotificationType.ERROR, __('No product data!')));

            return Promise.reject();
        }

        try {
            if (!cartId) {
                return await Promise.reject();
            }

            const { addProductsToCart: { user_errors: errors = [] } = {} } = await fetchMutation(
                CartQuery.getAddProductToCartMutation(cartId, products),
            );

            if (Array.isArray(errors) && errors.length > 0) {
                errors.forEach((error) => {
                    dispatch(showNotification(NotificationType.ERROR, getErrorMessage(error)));
                });

                await this.updateInitialCartData(dispatch);

                return await Promise.resolve();
            }

            await this.updateInitialCartData(dispatch);
            dispatch(showNotification(NotificationType.SUCCESS, __('Product was added to cart!')));
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(showNotification(NotificationType.ERROR, __('Not possible to fetch while offline')));

                return Promise.reject();
            }

            dispatch(showNotification(NotificationType.ERROR, getErrorMessage(error as NetworkError)));

            return Promise.reject();
        }

        return Promise.resolve();
    }
}

export default new CartDispatcher();
